import { REPORT_TITLES, type ReportName, type ReportsDto, type SummaryReport } from '@edms/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { Icon } from '../lib/icons';
import { useDepartmentsList } from '../lib/tasks-ui';
import { Empty, formatDate, relative } from '../lib/ui';

// ---------- Период ----------

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86_400_000));

const PRESETS: Array<{ label: string; range: () => [string, string] }> = [
  { label: '7 дней', range: () => [daysAgo(6), daysAgo(0)] },
  { label: '30 дней', range: () => [daysAgo(29), daysAgo(0)] },
  {
    label: 'Этот месяц',
    range: () => {
      const n = new Date();
      return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), iso(n)];
    },
  },
  {
    label: 'Прошлый месяц',
    range: () => {
      const n = new Date();
      return [iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)), iso(new Date(n.getFullYear(), n.getMonth(), 0))];
    },
  },
  {
    label: 'Год',
    range: () => [iso(new Date(new Date().getFullYear(), 0, 1)), daysAgo(0)],
  },
];

// ---------- Форматы ----------

const int = new Intl.NumberFormat('ru-RU');
const money = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/** Часы в понятном виде: «40 мин», «5,5 ч», «3,2 дн». */
export function fmtHours(h: number | null | undefined): string {
  if (h === null || h === undefined) return '—';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} мин`;
  if (h < 48) return `${(Math.round(h * 10) / 10).toLocaleString('ru-RU')} ч`;
  return `${(Math.round((h / 24) * 10) / 10).toLocaleString('ru-RU')} дн`;
}

const monthFmt = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' });
const fmtMonth = (m: string) => monthFmt.format(new Date(`${m}-01T12:00:00`));

// ---------- Графики ----------

/** Столбики по дням: создано и согласовано. Подписи по оси — не чаще, чем помещаются. */
function DayChart({ data }: { data: SummaryReport['byDay'] }) {
  const max = Math.max(1, ...data.flatMap((d) => [d.created, d.approved]));
  const W = 720;
  const H = 180;
  const pad = { l: 28, r: 8, t: 10, b: 24 };
  const cw = (W - pad.l - pad.r) / Math.max(1, data.length);
  const bw = Math.max(2, Math.min(14, cw / 2 - 2));
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const step = Math.ceil(data.length / 10);
  const ticks = [0, Math.ceil(max / 2), max].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <figure className="chart">
      <div className="legend">
        <span><i style={{ background: 'var(--chart-2)' }} /> Создано</span>
        <span><i style={{ background: 'var(--chart-1)' }} /> Согласовано</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Создано и согласовано по дням">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} className="grid" />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" className="axis">{t}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = pad.l + i * cw + cw / 2;
          return (
            <g key={d.day}>
              <title>{`${d.day.slice(8, 10)}.${d.day.slice(5, 7)}: создано ${d.created}, согласовано ${d.approved}`}</title>
              <rect x={x - bw - 1} y={y(d.created)} width={bw} height={Math.max(0, y(0) - y(d.created))} rx={2} fill="var(--chart-2)" />
              <rect x={x + 1} y={y(d.approved)} width={bw} height={Math.max(0, y(0) - y(d.approved))} rx={2} fill="var(--chart-1)" />
              {i % step === 0 && (
                <text x={x} y={H - 6} textAnchor="middle" className="axis">
                  {`${d.day.slice(8, 10)}.${d.day.slice(5, 7)}`}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

/** Горизонтальные полосы: сравнение по одному показателю, подпись значения справа. */
function Bars({ rows, format = (v) => int.format(v), tone = 'var(--chart-1)' }: { rows: Array<{ label: string; value: number; sub?: string; warn?: boolean }>; format?: (v: number) => string; tone?: string }) {
  const max = Math.max(1e-9, ...rows.map((r) => r.value));
  if (rows.length === 0) return null;
  return (
    <div className="bars">
      {rows.map((r, i) => (
        <div className="bar-row" key={`${r.label}-${i}`}>
          <span className="bar-label" title={r.label}>
            {r.label}
            {r.sub && <small className="faint"> · {r.sub}</small>}
          </span>
          <span className="bar-track">
            <span className="bar-fill" style={{ width: `${(r.value / max) * 100}%`, background: r.warn ? 'var(--warn)' : tone }} />
          </span>
          <span className="bar-value">{format(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------- Страница ----------

const TABS: Array<{ key: Exclude<ReportName, 'summary'>; hint: string }> = [
  { key: 'approvers', hint: 'Сколько решений, как быстро, кто чаще нарушает сроки' },
  { key: 'stages', hint: 'Этапы, на которых задачи задерживаются дольше всего' },
  { key: 'routes', hint: 'Итоги по маршрутам и самый долгий этап каждого' },
  { key: 'hanging', hint: 'Что ждёт решения прямо сейчас — от самых долгих' },
  { key: 'subtasks', hint: 'Кому из инициаторов чаще ставят подзадачи и кто' },
  { key: 'activity', hint: 'Кто заходит в систему, создаёт, решает и отвечает' },
  { key: 'trips', hint: 'Согласованные командировки по месяцам и отделам' },
];

function ExcelLink({ name, query }: { name: ReportName; query: Record<string, string> }) {
  const qs = new URLSearchParams(Object.entries(query).filter(([, v]) => v)).toString();
  return (
    <a className="btn" href={`/api/reports/${name}.xlsx?${qs}`} download={`${REPORT_TITLES[name]} ${query.from}_${query.to}.xlsx`}>
      <Icon name="download" size={18} /> Excel
    </a>
  );
}

function Kpi({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: string; tone?: 'warn' | 'danger' | 'brand' }) {
  return (
    <div className={`card kpi${tone ? ` kpi-${tone}` : ''}`}>
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      {hint && <span className="faint">{hint}</span>}
    </div>
  );
}

export function ReportsPage() {
  const [[from, to], setRange] = useState<[string, string]>(PRESETS[1]!.range);
  const [departmentId, setDepartmentId] = useState('');
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('approvers');
  const departments = useDepartmentsList();
  const query = { from, to, departmentId };
  const reports = useQuery({
    queryKey: ['reports', query],
    queryFn: () => api.get<ReportsDto>('/reports', query),
    enabled: Boolean(from && to && from <= to),
    placeholderData: (prev) => prev,
  });
  const r = reports.data;
  const s = r?.summary;
  const finished = s ? s.approved + s.returned + s.rejected : 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Отчёты</h1>
          <p className="muted">Сроки согласования, узкие места маршрутов, нагрузка и активность сотрудников</p>
        </div>
        <ExcelLink name="summary" query={query} />
      </div>

      <div className="card toolbar report-filters">
        <div className="presets" role="group" aria-label="Период">
          {PRESETS.map((p, i) => {
            const [f, t] = p.range();
            // Если два варианта совпали (30 дней = этот месяц), подсвечиваем первый.
            const active = f === from && t === to && PRESETS.findIndex((x) => x.range().join() === `${from},${to}`) === i;
            return (
              <button key={p.label} className={`tab${active ? ' active' : ''}`} onClick={() => setRange([f, t])}>
                {p.label}
              </button>
            );
          })}
        </div>
        <label className="inline-field">
          <span className="faint">с</span>
          <input className="input" type="date" value={from} max={to} onChange={(e) => setRange([e.target.value, to])} />
        </label>
        <label className="inline-field">
          <span className="faint">по</span>
          <input className="input" type="date" value={to} min={from} onChange={(e) => setRange([from, e.target.value])} />
        </label>
        <select className="select" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} aria-label="Отдел">
          <option value="">Все отделы</option>
          {departments.data?.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      {reports.isError && <div className="alert">{reports.error.message}</div>}

      <div className="kpis" aria-busy={reports.isFetching}>
        <Kpi label="Запущено" value={s ? int.format(s.launched) : '…'} hint={s ? `создано документов: ${s.created}` : undefined} />
        <Kpi label="Согласовано" value={s ? int.format(s.approved) : '…'} tone="brand" hint={s && finished ? `${Math.round((s.approved / finished) * 100)}% завершённых` : undefined} />
        <Kpi label="Не согласовано" value={s ? int.format(s.returned) : '…'} tone={s?.returned ? 'warn' : undefined} hint={s ? `отклонено: ${s.rejected}, прекращено: ${s.cancelled}` : undefined} />
        <Kpi label="Время согласования" value={fmtHours(s?.avgApprovalHours)} hint="в среднем от запуска до итога" />
        <Kpi label="Время решения" value={fmtHours(s?.avgDecisionHours)} hint="в среднем у одного согласующего" />
        <Kpi label="С нарушением срока" value={s ? int.format(s.overdueSteps) : '…'} tone={s?.overdueSteps ? 'danger' : undefined} hint="шагов согласования" />
        <Kpi label="Ждут решения" value={s ? int.format(s.pendingNow) : '…'} hint="сейчас, у всех согласующих" />
        <Kpi label="Поручения" value={s ? int.format(s.assignmentsDone) : '…'} hint={s ? `выполнено; просрочено: ${s.assignmentsOverdue}` : undefined} tone={s?.assignmentsOverdue ? 'warn' : undefined} />
      </div>

      {s && s.byDay.length > 1 && (
        <section className="card card-pad" style={{ marginTop: 16 }}>
          <h2>По дням</h2>
          <DayChart data={s.byDay} />
        </section>
      )}

      <section className="card" style={{ marginTop: 16 }}>
        <div className="card-pad" style={{ paddingBottom: 0 }}>
          <div className="tabs" role="tablist">
            {TABS.map((t) => (
              <button key={t.key} role="tab" aria-selected={tab === t.key} className={`tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
                {REPORT_TITLES[t.key]}
              </button>
            ))}
          </div>
          <div className="section-head">
            <p className="muted">{TABS.find((t) => t.key === tab)!.hint}</p>
            <ExcelLink name={tab} query={query} />
          </div>
        </div>
        {r && <ReportTab tab={tab} r={r} />}
      </section>
    </>
  );
}

function NoData() {
  return <Empty emoji="📭" title="За период данных нет">Выберите другой период или отдел</Empty>;
}

function ReportTab({ tab, r }: { tab: (typeof TABS)[number]['key']; r: ReportsDto }) {
  switch (tab) {
    case 'approvers': {
      const rows = r.approvers.filter((a) => a.avgHours !== null);
      if (r.approvers.length === 0) return <NoData />;
      return (
        <>
          {rows.length > 0 && (
            <div className="card-pad">
              <h3>Среднее время решения</h3>
              <Bars format={fmtHours} rows={rows.slice(0, 10).map((a) => ({ label: a.user.fullName, sub: a.department ?? undefined, value: a.avgHours!, warn: a.overdue > 0 }))} />
              <p className="faint">Оранжевым — у кого были нарушения срока</p>
            </div>
          )}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Согласующий</th>
                  <th className="num">Решений</th>
                  <th className="num">Согл. / не согл. / откл.</th>
                  <th className="num">Среднее</th>
                  <th className="num">Максимум</th>
                  <th className="num">С нарушением срока</th>
                  <th className="num">Ждут сейчас</th>
                  <th className="num">Дольше всего ждёт</th>
                </tr>
              </thead>
              <tbody>
                {r.approvers.map((a) => (
                  <tr key={a.user.id}>
                    <td><b>{a.user.fullName}</b><br /><span className="faint">{a.department ?? '—'}</span></td>
                    <td className="num">{a.decided}</td>
                    <td className="num">{a.approved} / {a.declined} / {a.rejected}</td>
                    <td className="num">{fmtHours(a.avgHours)}</td>
                    <td className="num">{fmtHours(a.maxHours)}</td>
                    <td className={`num${a.overdue ? ' overdue' : ''}`}>{a.overdue || '—'}</td>
                    <td className="num">{a.pendingNow || '—'}</td>
                    <td className="num">{fmtHours(a.oldestPendingHours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }
    case 'stages':
      if (r.stages.length === 0) return <NoData />;
      return (
        <>
          <div className="card-pad">
            <Bars format={fmtHours} rows={r.stages.filter((s) => s.avgHours !== null).slice(0, 10).map((s) => ({ label: s.stage, sub: s.route, value: s.avgHours!, warn: s.overdue > 0 }))} />
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Этап</th><th>Маршрут</th><th className="num">Среднее время</th><th className="num">Решений</th><th className="num">Не согл. / откл.</th><th className="num">С нарушением срока</th></tr>
              </thead>
              <tbody>
                {r.stages.map((s) => (
                  <tr key={`${s.route}-${s.stage}`}>
                    <td><b>{s.stage}</b></td>
                    <td>{s.route}</td>
                    <td className="num">{fmtHours(s.avgHours)}</td>
                    <td className="num">{s.decisions}</td>
                    <td className="num">{s.declines || '—'}</td>
                    <td className={`num${s.overdue ? ' overdue' : ''}`}>{s.overdue || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    case 'routes':
      if (r.routes.length === 0) return <NoData />;
      return (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Маршрут</th><th className="num">Запусков</th><th className="num">Согласовано</th><th className="num">Не согласовано</th><th className="num">Отклонено</th><th className="num">Среднее время</th><th>Самый долгий этап</th></tr>
            </thead>
            <tbody>
              {r.routes.map((x) => (
                <tr key={x.route}>
                  <td><b>{x.route}</b></td>
                  <td className="num">{x.launched}</td>
                  <td className="num">{x.approved}</td>
                  <td className="num">{x.returned || '—'}</td>
                  <td className="num">{x.rejected || '—'}</td>
                  <td className="num">{fmtHours(x.avgHours)}</td>
                  <td>{x.slowestStage ? <>{x.slowestStage} <span className="faint">· {fmtHours(x.slowestStageHours)}</span></> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'hanging':
      if (r.hanging.length === 0) return <Empty emoji="✨" title="Ничего не зависло">Все шаги согласования решены</Empty>;
      return (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Задача</th><th>Ждёт решения</th><th>Этап</th><th>С</th><th>Срок</th><th className="num">Ждёт</th></tr>
            </thead>
            <tbody>
              {r.hanging.map((h) => (
                <tr key={`${h.taskId}-${h.user.id}-${h.stage}`}>
                  <td><Link to={`/tasks/${h.taskId}`}>№{h.number} {h.title}</Link><br /><span className="faint">{h.route ?? ''}</span></td>
                  <td>{h.user.fullName}</td>
                  <td>{h.stage}</td>
                  <td>{formatDate(h.activatedAt)}</td>
                  <td className={h.overdue ? 'overdue' : undefined}>{formatDate(h.dueAt)}</td>
                  <td className={`num${h.overdue ? ' overdue' : ''}`}>{fmtHours(h.hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'subtasks':
      if (r.subtasks.length === 0) return <NoData />;
      return (
        <>
          <div className="card-pad">
            <Bars rows={r.subtasks.slice(0, 10).map((s) => ({ label: s.initiator.fullName, value: s.subtasks, sub: `задач: ${s.tasks}` }))} />
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Инициатор</th><th className="num">Его задач</th><th className="num">Подзадач по ним</th><th>Чаще ставят подзадачи</th><th className="num">Возвратов на доработку</th></tr>
              </thead>
              <tbody>
                {r.subtasks.map((s) => (
                  <tr key={s.initiator.id}>
                    <td><b>{s.initiator.fullName}</b></td>
                    <td className="num">{s.tasks}</td>
                    <td className="num">{s.subtasks}</td>
                    <td>{s.topCreators.map((c) => `${c.user.fullName} (${c.count})`).join(', ')}</td>
                    <td className="num">{s.returnedForRework || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    case 'activity':
      if (r.activity.length === 0) return <NoData />;
      return (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Сотрудник</th><th>Последний визит</th><th className="num">Входов</th><th className="num">Создал</th><th className="num">Решений</th><th className="num">Комментариев</th><th className="num">Ответов</th><th className="num">Ждут сейчас</th></tr>
            </thead>
            <tbody>
              {r.activity.map((a) => {
                const stale = a.pendingNow > 0 && (!a.lastSeenAt || Date.now() - new Date(a.lastSeenAt).getTime() > 86_400_000);
                return (
                  <tr key={a.user.id}>
                    <td><b>{a.user.fullName}</b><br /><span className="faint">{a.department ?? '—'}</span></td>
                    <td className={stale ? 'overdue' : undefined} title={stale ? 'Задачи ждут, а сотрудник давно не заходил' : undefined}>{relative(a.lastSeenAt)}</td>
                    <td className="num">{a.logins || '—'}</td>
                    <td className="num">{a.created || '—'}</td>
                    <td className="num">{a.decisions || '—'}</td>
                    <td className="num">{a.comments || '—'}</td>
                    <td className="num">{a.answered || '—'}</td>
                    <td className="num">{a.pendingNow || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    case 'trips': {
      if (r.trips.length === 0) return <NoData />;
      const total = r.trips.reduce((s, t) => s + t.total, 0);
      return (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Месяц</th><th>Отдел</th><th className="num">Командировок</th><th className="num">Дней</th><th className="num">Сумма по сметам</th></tr>
            </thead>
            <tbody>
              {r.trips.map((t) => (
                <tr key={`${t.month}-${t.department}`}>
                  <td>{fmtMonth(t.month)}</td>
                  <td>{t.department ?? 'Без отдела'}</td>
                  <td className="num">{t.trips}</td>
                  <td className="num">{t.days}</td>
                  <td className="num">{money.format(t.total)} ₽</td>
                </tr>
              ))}
              <tr>
                <td colSpan={4}><b>Итого</b></td>
                <td className="num"><b>{money.format(total)} ₽</b></td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }
  }
}
